import { consoleRoutes, LEAD_OWNER_MODES, ROUND_ROBIN_SETTINGS, ROUTE_MODES, type LeadOwnerMode, type RoundRobinSetting, type RouteMode } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Select } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../../session/workspace-context";
import type { AssignLeadOwnerStep, RouteHandoffStep } from "../../types";
import { OptionSelect } from "../action-forms";
import { useRefs } from "../refs";
import type { FormProps } from "../webhook-form";

const R = "engage.workflows.system.route";
const O = "engage.workflows.system.owner";
const E = "engage.workflows.system.endpoints";
const F = "engage.workflows.fields";

/** Route the handoff: who a new handoff goes to, decided when it starts (a default workflow only). */
export function RouteHandoffForm({ step, disabled, onChange }: FormProps<RouteHandoffStep>) {
  const { t } = useTranslation();
  const { teams, members } = useRefs();
  const pick = (mode: RouteMode) => {
    onChange({ id: step.id, name: step.name, type: "route_handoff", mode, ...(mode === "team" ? { round_robin: step.round_robin ?? "team_setting" } : {}) });
  };
  return (
    <div className="space-y-4">
      <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted">{t(`${R}.note`)}</p>
      <Field label={t(`${R}.mode`)}>
        <Select value={step.mode} disabled={disabled} onChange={(e) => { pick(e.target.value as RouteMode); }}>
          {ROUTE_MODES.map((mode) => <option key={mode} value={mode}>{t(`${R}.modes.${mode}`)}</option>)}
        </Select>
      </Field>
      {step.mode === "team" ? <OptionSelect label={t(`${F}.team`)} value={step.team_id} options={teams} disabled={disabled} empty={t(`${F}.choose`)} onChange={(team_id) => { onChange({ ...step, team_id }); }} /> : null}
      {step.mode === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
      {step.mode === "team" ? (
        <Field label={t(`${R}.roundRobin`)} hint={t(`${R}.roundRobinHint`)}>
          <Select value={step.round_robin ?? "team_setting"} disabled={disabled} onChange={(e) => { onChange({ ...step, round_robin: e.target.value as RoundRobinSetting }); }}>
            {ROUND_ROBIN_SETTINGS.map((setting) => <option key={setting} value={setting}>{t(`${R}.roundRobins.${setting}`)}</option>)}
          </Select>
        </Field>
      ) : null}
      {step.mode === "agent_settings" ? <p className="text-xs text-fg-subtle">{t(`${R}.agentSettingsHint`)}</p> : null}
    </div>
  );
}

/** Assign the lead's owner: who a new lead goes to, decided when it is captured (a default workflow only). */
export function AssignLeadOwnerForm({ step, disabled, onChange }: FormProps<AssignLeadOwnerStep>) {
  const { t } = useTranslation();
  const { members, leadOwners } = useRefs();
  return (
    <div className="space-y-4">
      <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted">{t(`${O}.note`)}</p>
      <Field label={t(`${O}.mode`)}>
        <Select value={step.mode} disabled={disabled} onChange={(e) => { onChange({ id: step.id, name: step.name, type: "assign_lead_owner", mode: e.target.value as LeadOwnerMode }); }}>
          {LEAD_OWNER_MODES.map((mode) => <option key={mode} value={mode}>{t(`${O}.modes.${mode}`)}</option>)}
        </Select>
      </Field>
      {step.mode === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={leadOwners ?? members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
      {step.mode === "workspace_setting" ? <p className="text-xs text-fg-subtle">{t(`${O}.workspaceSettingHint`)}</p> : null}
    </div>
  );
}

/** Send to webhook endpoints: it has nothing to set; the endpoints' own subscriptions decide what they receive. */
export function SendToEndpointsForm() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="space-y-3">
      <p className="text-sm">{t(`${E}.body`)}</p>
      <p className="text-xs text-fg-subtle">
        {t(`${E}.hint`)}{" "}
        <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.settings(summary.slug, "webhooks")}>{t(`${E}.link`)}</AppLink>
      </p>
    </div>
  );
}
